/**
 * CourseScreen is the main entry point for students to access their modules.
 * It displays:
 * - Currently selected module with details
 * - List of available modules
 * - Module state (locked, in progress, completed)
 *
 * The screen manages module selection and handles transitions between
 * different module states (selection, grading, completion).
 */

import React, { useRef } from 'react';

import {
  Box, Heading, Center, Spinner, useMedia,
} from '@gluestack-ui/themed';
import { RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { useTranslation } from 'react-i18next';
import ScrollableScreenContainer from '../../common/components/ScrollableScreenContainer';
import ModuleList from './components/ModuleList';
import SelectedModuleCard from './components/SelectedModuleCard';
import Greeting from './components/Greeting';
import { useDialog } from '../../common/context/DialogContext';

import { useModuleSelectionState } from '../../common/context/AppStateStore';
import { useAuthStore } from '../../common/auth/store';

import { useActivity } from '../../common/hooks/student/useActivity';
import { useCourseData } from '../../common/hooks/student/useCourseData';

import { getColorForModule } from '../../common/utils/getColorForModule';
import { getModuleState } from '../../common/utils/moduleStateLegacy';
import { useSelectedModule } from '../../common/hooks/student/useSelectedModule';
import log from '../../common/utils/logger';

export const CourseScreen = () => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const navigation = useNavigation();
  const logout = useAuthStore((state) => state.logout);
  const { logSelectModule } = useActivity();

  const { selectedModule, setSelectedModule } = useModuleSelectionState();

  const {
    modules, isLoading: isLoadingModules, error, refetchCourseData,
  } = useCourseData();

  const { module: currentModule } = useSelectedModule();

  const [refreshing, setRefreshing] = React.useState(false);

  const user = useAuthStore((state) => state.user);

  const { showToastMessage, showDialog } = useDialog();
  const media = useMedia();

  React.useEffect(() => {
    if (error) {
      showToastMessage('error', t('student.course.errors.server-error'));
      log.info('Cannot access list of modules', error);
    }
  }, [error, showToastMessage, t]);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    refetchCourseData().then(() => {
      setRefreshing(false);
    });
  }, [refetchCourseData]);

  if (isLoadingModules) {
    return (
      <Center testID="loading-container" w="100%" h="100%">
        <Spinner testID="loading-indicator" size="large" />
      </Center>
    );
  }

  /**
   * Internal helper to handle the actual module opening process
   * after confirmation dialogs.
   */
  const openModule = ({ moduleId, courseId }) => {
    if (moduleId && courseId) {
      logSelectModule({ moduleId, courseId });
      showToastMessage(
        'success',
        t('student.course.open-module-dialog.message-success', {
          newModuleTitle: currentModule?.title,
        }),
      );
      scrollRef?.current.scrollTo({ y: 0, animated: true });

      setSelectedModule({ moduleId, courseId });
    } else {
      log.info('Error, cannot open module', { moduleId, courseId });
    }
  };

  /**
   * Handles opening a module, with different flows for:
   * - Graded modules (navigate to grade screen)
   * - Locked modules (show warning)
   * - New module selection (show confirmation)
   */
  const handleOpenModule = ({ moduleId, courseId }) => {
    if (!moduleId || !courseId) {
      return;
    }
    const module = modules.find((m) => m.id === moduleId && m.courses[0].id === courseId);
    if (!module) {
      return;
    }
    // if it's a graded module, navigate to the grade screen
    if (module.state.studentWorkflowState === 'graded') {
      navigation.navigate('GradeScreen', { moduleId, courseId });
      return;
    }

    if (module.state.studentWorkflowState === 'submitted') {
      return;
    }

    // if no module is selected, open it
    if (!selectedModule?.moduleId || !selectedModule?.courseId) {
      openModule({ moduleId, courseId });
      return;
    }
    // if module is locked, show unlock warning dialog
    if (getModuleState(module, modules) === 'locked') {
      showDialog({
        size: 'lg',
        title: t('student.course.locked-module-dialog.title'),
        message: t('student.course.locked-module-dialog.message', {
          moduleTitle: module.title,
        }),
        buttons: [
          {
            text: t('student.course.locked-module-dialog.button-open'),
            action: 'secondary',
            onPress: () => openModule({ moduleId, courseId }),
          },
          {
            text: t('student.course.locked-module-dialog.button-cancel'),
            action: 'primary',
          },
        ],
      });
      return;
    }

    // or: show dialog to confirm opening a new module
    showDialog({
      size: 'lg',
      title: t('student.course.open-module-dialog.title'),
      message: t('student.course.open-module-dialog.message', {
        oldModuleTitle: currentModule?.title,
        newModuleTitle: module.title,
      }),
      buttons: [
        {
          text: t('student.course.open-module-dialog.button-open'),
          action: 'secondary',
          onPress: () => openModule({ moduleId, courseId }),
        },
        {
          text: t('student.course.open-module-dialog.button-cancel'),
          action: 'primary',
        },
      ],
    });
  };

  /**
   * Navigates to ModuleScreen for working on the currently selected module.
   * If the module is already graded, navigates to GradeScreen instead.
   */
  const handleWorkOnModule = (module) => {
    const moduleId = module.id;
    const courseId = module.courses?.[0]?.id;

    if (module.state?.studentWorkflowState === 'graded') {
      navigation.navigate('GradeScreen', { moduleId, courseId });
    } else if (module.state?.studentWorkflowState === 'submitted') {
      // stay on course screen
    } else {
      navigation.navigate('ModuleScreen', { moduleId, courseId });
    }
  };

  // Remove selected module only from its specific course.
  // Terminal states must stay visible in the module list because
  // SelectedModuleCard intentionally hides submitted and graded modules.
  const notSelectedModules = modules
    ?.map((module) => {
      // If this module is the selected one, only remove it from the course it was selected in
      if (module.id === selectedModule?.moduleId) {
        const isTerminalState = module.state?.studentWorkflowState === 'submitted'
          || module.state?.studentWorkflowState === 'graded';
        if (isTerminalState) {
          return module;
        }
        return {
          ...module,
          courses: module.courses.filter((course) => course.id !== selectedModule?.courseId),
        };
      }
      return module;
    })
    .filter((module) => module.courses.length > 0) || [];

  return (
    <ScrollableScreenContainer
      testID="course-screen-scroll-view"
      scrollRef={scrollRef}
      px="$2"
      bg="$bgApp"
      refreshControl={<RefreshControl testID="refresh-control" refreshing={refreshing} onRefresh={onRefresh} />}
    >
      <Greeting testID="greeting-component" user={user} showMenu={!media.md} handleLogout={logout} />

      {currentModule && (
        <Box testID="selected-module-section" justifyContent="space-between" $xl-flexDirection="row" mb={36}>
          <Heading testID="current-module-heading" size="md" mb={22}>
            {t('student.course.modulestages.current')}
          </Heading>
          <SelectedModuleCard
            testID={`selected-module-card-${currentModule.id}`}
            color={getColorForModule(currentModule)}
            imageUrl={currentModule?.imageUrl}
            title={currentModule?.title}
            description={currentModule.metadata.description}
            levels={currentModule.metadata.levels}
            lecturer={
              currentModule.author
                ? `${currentModule.author.title || ''} ${currentModule.author.firstname || ''} ${
                  currentModule.author.lastname || ''
                }`.trim()
                  || currentModule.author.name
                  || ''
                : ''
            }
            grade={currentModule.grading?.grade}
            isGraded={currentModule.state?.studentWorkflowState === 'graded'}
            isSubmitted={currentModule.state?.studentWorkflowState === 'submitted'}
            handleWorkOnModule={() => handleWorkOnModule(currentModule)}
          />
        </Box>
      )}
      <ModuleList testID="module-list" modules={notSelectedModules} handleOpenModule={handleOpenModule} />
    </ScrollableScreenContainer>
  );
};
export default CourseScreen;
